<template>
  <Section
    :id="id"
    :header="
      t('common.createdParam', {
        param: t('shipments.shipments', createdShipmentIds.length),
      })
    "
    :loading="isLoading"
    class="route-builder-executed-shipments-section"
  >
    <template v-if="!!error" #backButton>
      <RygenButton
        mode="tertiary"
        class="back-button"
        :icon="PrimeIcons.ARROW_LEFT"
        :label="
          t('common.backToParam', {
            param:
              request.rateQuoteGroupType === 'none'
                ? t('routeBuilder.planning')
                : t('common.rating'),
          })
        "
        @click="emit('back')"
      />
    </template>

    <template #default>
      <RouteBuilderPhase2RateShopCard
        :id="`${request.rateQuoteGroupType}-rates`"
        :type="request.rateQuoteGroupType"
        :mileage="totalMileage"
        :rate-quotes="shipmentRequests"
        :current-step="request.currentStep"
        expanded
      />
    </template>

    <template v-if="searchRoute" #footer>
      <RygenButton
        mode="primary"
        as="router-link"
        :icon="PrimeIcons.MAP"
        :label="
          t('common.viewParam', {
            param: t('shipments.shipments', createdShipmentIds.length),
          })
        "
        :to="searchRoute"
      />
    </template>
  </Section>
</template>

<script lang="ts" setup>
import { PrimeIcons } from '@primevue/core/api';
import { AxiosError } from 'axios';
import type { PropType, Ref } from 'vue';
import { computed, onMounted, ref, toRefs } from 'vue';
import { useI18n } from 'vue-i18n';

import { useToast } from '@rygen/rygen-ui/composables';
import { ErrorResponse, Loadable } from '@rygen/rygen-ui/model';
import { notNull } from '@rygen/rygen-ui/util';

import api from '@/api';
import {
  RouteBuilderPhase2Request,
  RoutePlanShipmentRequest,
} from '@/model/routeBuilder/phase2';
import { buildSearchRoute } from '@/util';

import RouteBuilderPhase2RateShopCard from '@/components/routeBuilder/rateShop/RouteBuilderPhase2RateShopCard.vue';
import RygenButton from '@rygen/rygen-ui/components/button/RygenButton.vue';
import Section from '@rygen/rygen-ui/components/ui/Section.vue';

const SHIP_ORDER_PLANNING_EXCEPTION_CODE =
  'com.rygen.corsair.domain.shipmentplanning.shiporder.entity.ShipOrderPlanningException';

const props = defineProps({
  id: {
    type: String,
    required: true,
  },
  request: {
    type: Object as PropType<RouteBuilderPhase2Request>,
    required: true,
  },
});
const { id, request } = toRefs(props);
const emit = defineEmits<{
  (event: 'back'): void;
}>();

const { t } = useI18n();
const toast = useToast();

const isLoading = ref(false);
const error: Ref<unknown> = ref();

const shipmentRequests = computed(
  (): Array<Loadable<RoutePlanShipmentRequest>> => {
    return request.value.shipmentRequests.map((s) => ({
      loading: isLoading.value,
      data: s,
      error: error.value,
    }));
  },
);

const createdShipmentIds = computed(() => {
  return request.value.shipmentRequests
    .map((s) => s.result?.shipmentId)
    .filter(notNull);
});

const searchRoute = computed(() => {
  return createdShipmentIds.value.length
    ? buildSearchRoute('shipmentList', { shipmentId: createdShipmentIds.value })
    : undefined;
});

const totalMileage = computed(() => {
  const mileage = request.value.shipmentRequests
    .map((s) => s.result?.mileage ?? 0)
    .filter(notNull)
    .reduce((a, b) => a + b, 0);

  return Math.round(mileage);
});

// The commit (materializing the plan's shipments) is intentionally tied to this
// component mounting — reaching the executing step *is* the trigger, so the side
// effect can't drift away from the step transition.
const createRoutePlan = async (request: RouteBuilderPhase2Request) => {
  try {
    isLoading.value = true;
    const results = await api.routePlan.createRoutePlan(request);
    if (results.routePlanId != null) {
      request.routePlanId = results.routePlanId;
    }
    request.shipmentRequests.forEach((s) => {
      s.result = results.shipments.find(
        (r) => r.shipmentIdentifier === s.shipmentIdentifier,
      );
    });
  } catch (e) {
    const err = e as AxiosError<ErrorResponse, any>;
    error.value = err;

    console.error('Error creating route plan', e);

    if (
      err.response?.data.exceptionCode === SHIP_ORDER_PLANNING_EXCEPTION_CODE
    ) {
      toast.addError({
        detail: t('routeBuilder.errors.alreadyPlannedShipOrder'),
        error: e,
      });
    } else {
      toast.addError({
        detail: t('errors.creatingParam', {
          param: t('shipments.shipments', 2),
        }),
        error: e,
      });
    }
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  createRoutePlan(request.value);
});
</script>

<style>
.route-builder-executed-shipments-section {
  .section-header {
    .section-header-left {
      flex-flow: column;
      align-items: flex-start;
    }
  }

  .section-data {
    display: flex;
    flex-flow: column;
    gap: var(--gap-1);
    height: 100%;
    overflow: auto;
    padding: var(--route-builder-rate-shop-padding);
  }

  .section-footer {
    display: flex;
    justify-content: flex-end;
  }
}
</style>
